import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { Stack, Typography } from '@mui/material'
import CodeMirror, { EditorView } from '@uiw/react-codemirror'
import { memo } from 'react'
import { IFlow, INode } from '../../../../definitions/Nodes'
import NodeCodemirrorManager from '../../../../managers/NodeCodemirrorManager'
import NodeManager from '../../../../managers/NodeManager'
import { useAppSelector } from '../../../../store/Hooks'

interface P {
    flow: IFlow
    selectedNode: INode<'fork'>
    setSelected: (node: INode<'fork'>) => void
    disable: boolean
}

const logicKeywords: Completion[] = [
    { label: 'AND', type: 'logical', info: '-> logical operator' },
    { label: 'OR', type: 'logical', info: '-> logical operator' },
    { label: '>', type: 'operator', info: '-> comparison operator' },
    { label: '<', type: 'operator', info: '-> comparison operator' },
    { label: '>=', type: 'operator', info: '-> comparison operator' },
    { label: '<=', type: 'operator', info: '-> comparison operator' },
    { label: '=', type: 'operator', info: '-> comparison operator' },
    { label: '!=', type: 'operator', info: '-> comparison operator' }
]

const NodeForkEditor = ({ flow, selectedNode, setSelected, disable }: P) => {
    const themeMode = useAppSelector(state => state.app.mode)

    const inputDataShape = NodeManager.getMappedShapes(NodeManager.getInputShapes<'fork'>(selectedNode, flow.nodes).find(s => s.argKey === 'data'))

    const fieldKeywords: Completion[] = inputDataShape.length
        ? inputDataShape.map(x => ({
            label: x,
            type: 'fieldInput',
            info: '-> Field Input'
        }))
        : []

    const nodeKeywords = NodeCodemirrorManager.keywords(selectedNode, flow)

    const fixedHeightEditor = EditorView.theme({
        '&': { height: 300 },
        '.cm-scroller': { overflowY: 'auto' },
        '&.cm-editor': { borderRadius: 'var(--unit)', padding: 'var(--unit)', backgroundColor: themeMode === 'light' ? '#F5F5F5' : undefined }
    })

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...logicKeywords, ...nodeKeywords, ...fieldKeywords]
        }
    }

    return (
        <Stack spacing={3}>
            <Typography variant='h6'>Insert the condition:</Typography>
            <CodeMirror
                placeholder={`Insert the name of a node and access his field then write your condition. \n\nE.g. @{nodeName.field1} > 52 AND @{nodeName.field2} = 'price'`}
                readOnly={disable}
                theme={themeMode === 'dark' ? 'dark' : 'light'}
                value={selectedNode.data.condition ? NodeCodemirrorManager.changeLabelToUuid(selectedNode.data.condition, nodeKeywords, true) : ''}
                width='100%'
                style={{ flex: 1, height: '100%', display: 'flex' }}
                minHeight='30vh'
                extensions={[fixedHeightEditor, EditorView.lineWrapping, autocompletion({ override: [myCompletions] })]}
                basicSetup={{ lineNumbers: false, foldGutter: false, highlightActiveLine: false }}
                onChange={newValue => {
                    const parsedStr = NodeCodemirrorManager.changeLabelToUuid(newValue, nodeKeywords)
                    setSelected({
                        ...selectedNode,
                        data: {
                            ...selectedNode.data,
                            condition: parsedStr
                        }
                    })
                }}
            />
        </Stack>
    )
}

export default memo(NodeForkEditor)
